import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import DealForgeLogo from "../components/DealForgeLogo";
import {
  FileSignature, CheckCircle, AlertTriangle, Eraser,
  UserPlus, Lock, Shield, FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicDocSignPage() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => { fetchDoc(); }, [token]);
  useEffect(() => { if (doc && canvasRef.current) initCanvas(); }, [doc]);

  const fetchDoc = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portal-docs/public/sign/${token}`);
      setDoc(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "This signing link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left;
    const y = (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top;
    return { x, y };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    initCanvas();
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!signerName.trim()) { toast.error("Please enter your full name"); return; }
    if (!hasSignature) { toast.error("Please draw your signature"); return; }
    if (!agreed) { toast.error("You must agree to the terms"); return; }

    setSigning(true);
    try {
      const signatureData = canvasRef.current.toDataURL("image/png");
      const res = await axios.post(`${BACKEND_URL}/api/portal-docs/public/sign/${token}`, {
        signer_name: signerName,
        signer_email: signerEmail,
        signature_data: signatureData,
        agreed_to_terms: true,
      });
      setSigned(true);
      setInviteToken(res.data.invite_token);
      toast.success("Document signed successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sign document");
    } finally {
      setSigning(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!signerEmail) { toast.error("Email required to create account"); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }
    setCreatingAccount(true);
    try {
      await axios.post(`${BACKEND_URL}/api/signer/create-account`, {
        invite_token: inviteToken,
        password,
        email: signerEmail,
      });
      setAccountCreated(true);
      toast.success("Account created! Welcome to DealForge.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally {
      setCreatingAccount(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-6">
      <div className="mb-8"><DealForgeLogo size="md" /></div>
      <div className="max-w-md w-full bg-[#0A0A0A] border border-[#FF3B30]/30 rounded-2xl p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
        <h2 className="text-white text-xl font-bold mb-2">Link Unavailable</h2>
        <p className="text-white/50 text-sm">{error}</p>
      </div>
    </div>
  );

  if (signed) return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-6">
      <div className="mb-8"><DealForgeLogo size="md" /></div>
      <div className="max-w-md w-full">
        <div className="bg-[#0A0A0A] border border-emerald-500/30 rounded-2xl p-8 text-center mb-4">
          <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-[#00FF88]" />
          </div>
          <h2 className="text-white text-xl font-bold mb-1">Signed Successfully</h2>
          <p className="text-white/40 text-sm">
            <strong className="text-white">{doc?.filename}</strong> has been signed by <strong className="text-[#00FF88]">{signerName}</strong>
          </p>
          <p className="text-white/30 text-xs mt-2">{doc?.share_sender_name} will be notified.</p>
        </div>

        {!accountCreated && (
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-2xl p-6">
            {!showCreateAccount ? (
              <div className="text-center">
                <UserPlus className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-1">Join DealForge</h3>
                <p className="text-white/40 text-xs mb-4">
                  Create a free account to access deal flow, documents, and tools with {doc?.share_sender_name}.
                </p>
                <button onClick={() => setShowCreateAccount(true)}
                  className="w-full py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold text-sm rounded-lg transition-all"
                  data-testid="create-account-btn">
                  Create Free Account
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <h3 className="text-white font-semibold text-sm">Create Your Account</h3>
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Email</label>
                  <Input value={signerEmail} onChange={e => setSignerEmail(e.target.value)} type="email"
                    className="bg-white/[0.03] border-white/[0.08] text-white h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Password</label>
                  <Input value={password} onChange={e => setPassword(e.target.value)} type="password"
                    className="bg-white/[0.03] border-white/[0.08] text-white h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-white/40 block mb-1">Confirm Password</label>
                  <Input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} type="password"
                    className="bg-white/[0.03] border-white/[0.08] text-white h-9 text-sm" />
                </div>
                <button onClick={handleCreateAccount} disabled={creatingAccount}
                  className="w-full py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold text-sm rounded-lg transition-all disabled:opacity-50"
                  data-testid="submit-create-account-btn">
                  {creatingAccount ? "Creating..." : "Create Account"}
                </button>
              </div>
            )}
          </div>
        )}

        {accountCreated && (
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-2xl p-6 text-center">
            <CheckCircle className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
            <h3 className="text-white font-semibold">Account Created!</h3>
            <p className="text-white/40 text-xs mt-1">
              Welcome to DealForge. <a href="/login" className="text-[#D4AF37] hover:underline">Sign in to your account →</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block mb-6"><DealForgeLogo size="md" /></div>
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl px-5 py-3 inline-flex items-center gap-3">
            <FileText className="w-4 h-4 text-white/40" />
            <div className="text-left">
              <p className="text-white font-medium text-sm" data-testid="doc-filename">{doc?.filename}</p>
              <p className="text-white/40 text-xs">
                Signature requested by <span className="text-[#D4AF37]">{doc?.share_sender_name}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Signer Info */}
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-5">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-4">Your Information</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] text-white/40 block mb-1">Full Name <span className="text-[#FF3B30]">*</span></label>
                <Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Your full legal name"
                  className="bg-white/[0.03] border-white/[0.08] text-white h-9 text-sm" data-testid="signer-name-input" />
              </div>
              <div>
                <label className="text-[10px] text-white/40 block mb-1">Email (optional)</label>
                <Input value={signerEmail} onChange={e => setSignerEmail(e.target.value)} type="email" placeholder="you@example.com"
                  className="bg-white/[0.03] border-white/[0.08] text-white h-9 text-sm" data-testid="signer-email-input" />
              </div>
            </div>
          </div>

          {/* Document preview */}
          {doc?.extracted_text && (
            <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-5">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Document Preview</p>
              <div className="bg-white/[0.02] rounded-lg p-4 max-h-48 overflow-y-auto">
                <pre className="text-white/50 text-xs font-mono whitespace-pre-wrap leading-relaxed">
                  {doc.extracted_text.slice(0, 2000)}{doc.extracted_text.length > 2000 ? "\n...[document continues]" : ""}
                </pre>
              </div>
            </div>
          )}

          {/* Signature Pad */}
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Your Signature</p>
              <button onClick={clearCanvas} className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors">
                <Eraser className="w-3.5 h-3.5" /> Clear
              </button>
            </div>
            <div className="border-2 border-dashed border-white/[0.12] rounded-xl overflow-hidden bg-white relative">
              <canvas
                ref={canvasRef}
                width={600}
                height={180}
                data-testid="signature-canvas"
                className="w-full cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              {!hasSignature && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-gray-300 text-sm">Draw your signature here</p>
                </div>
              )}
            </div>
          </div>

          {/* Agreement + Sign */}
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-5 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#D4AF37] flex-shrink-0" data-testid="agree-terms-checkbox" />
              <span className="text-white/50 text-xs leading-relaxed">
                By checking this box and signing, I agree that my electronic signature is legally binding and equivalent to a handwritten signature. I have read and understood the document above.
              </span>
            </label>

            <button
              onClick={handleSign}
              disabled={signing || !agreed || !hasSignature || !signerName.trim()}
              data-testid="submit-signature-btn"
              className="w-full py-3.5 bg-[#D4AF37] hover:bg-[#C4A030] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2">
              {signing ? (
                <><span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Signing...</>
              ) : (
                <><FileSignature className="w-4 h-4" /> Sign Document</>
              )}
            </button>

            <div className="flex items-center justify-center gap-4 pt-1">
              <div className="flex items-center gap-1.5 text-white/20 text-[10px]">
                <Shield className="w-3 h-3" /> 256-bit Encrypted
              </div>
              <div className="flex items-center gap-1.5 text-white/20 text-[10px]">
                <Lock className="w-3 h-3" /> IP Logged
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
